import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { ExternalLink } from "react-feather";
import Select, { Option } from "@cs/components/src/Select";
import Button from "@cs/components/src/Button";
import Input from "@cs/components/src/Input";
import Banner from "@cs/components/src/Banner";
import Section from "@cs/components/src/Section";
import ClickOutside from "@cs/components/src/ClickOutside";
import ApiService from "../../services/ApiService";

const EXTENSION_ID = process.env.REACT_APP_ICONIK_EXTENSION_ID;
const CREDS_KEY = "iconik_launch_creds";

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 10px;
`;

const SelectWrapper = styled.div`
  min-width: 250px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

interface LaunchData {
  token: string;
  appId: string;
  systemDomainId: string;
  domainName: string;
  baseUrl: string;
  userId?: string;
}

/**
 * Structured MFA-required response the backend returns when iconik signals that OTP
 * fields are needed.  The backend returns HTTP 200 with this shape instead of a 4xx so
 * the frontend can cleanly reveal the OTP step.
 */
interface MfaRequiredData {
  mfaRequired: true;
  otpType: string;
}

interface Creds {
  email: string;
  password: string;
}

const chromeRuntime = () => (window as any).chrome?.runtime;

const loadCreds = (): Creds | null => {
  try {
    const raw = sessionStorage.getItem(CREDS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveCreds = (creds: Creds) =>
  sessionStorage.setItem(CREDS_KEY, JSON.stringify(creds));

const clearCreds = () => sessionStorage.removeItem(CREDS_KEY);

const SelectOrganization = () => {
  const { systemDomainId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [extensionReady, setExtensionReady] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  // MFA step state — set when backend signals mfaRequired
  const [mfaRequired, setMfaRequired] = useState(false);
  const [otpType, setOtpType] = useState("totp");
  const [otp, setOtp] = useState("");

  // Pending creds saved during the first (no-OTP) attempt so the second POST reuses them
  const pendingCredsRef = useRef<Creds | null>(null);

  const pingRef = useRef(false);

  useEffect(() => {
    if (!EXTENSION_ID || pingRef.current) {
      return;
    }
    pingRef.current = true;
    const rt = chromeRuntime();
    if (!rt) {
      return;
    }
    rt.sendMessage(EXTENSION_ID, { type: "PING" }, (response: any) => {
      if (!rt.lastError && response?.ok) {
        setExtensionReady(true);
      }
    });
  }, []);

  const closeModal = () => {
    setShowModal(false);
    setMfaRequired(false);
    setOtp("");
    setOtpType("totp");
    pendingCredsRef.current = null;
  };

  /**
   * Send the launch request.  On the first call (no OTP) the backend either returns
   * launch creds (non-MFA account) or { mfaRequired: true } (MFA account).  On the
   * second call the caller supplies otp_type + otp and we expect launch creds.
   */
  const launch = async (creds: Creds, mfaOtpType?: string, mfaOtp?: string) => {
    if (!systemDomainId || !EXTENSION_ID) {
      return;
    }
    setLaunching(true);
    setError(null);
    try {
      const body: Record<string, string> = {
        email: creds.email,
        password: creds.password,
      };
      if (mfaOtpType !== undefined) {
        body.otp_type = mfaOtpType;
      }
      if (mfaOtp !== undefined) {
        body.otp = mfaOtp;
      }

      const { data } = await ApiService.fetch<{
        data: LaunchData | MfaRequiredData;
      }>("POST", `/api/config/organization/${systemDomainId}/launch`, body);

      // Detect MFA-required signal from backend
      if ((data as MfaRequiredData).mfaRequired) {
        const mfaData = data as MfaRequiredData;
        pendingCredsRef.current = creds;
        setOtpType(mfaData.otpType ?? "totp");
        setOtp("");
        setMfaRequired(true);
        // Show the modal so the OTP input step is visible (important when launching
        // with saved credentials, where the modal was never opened).
        setShowModal(true);
        return;
      }

      // Success — launch in iconik
      const launchData = data as LaunchData;
      saveCreds(creds);
      closeModal();
      chromeRuntime().sendMessage(EXTENSION_ID, {
        type: "ICONIK_LAUNCH",
        ...launchData,
      });
    } catch (e: any) {
      if (e.message === "Invalid iconik credentials") {
        clearCreds();
        setError("Wrong email or password.");
      } else if (
        e.message?.startsWith("iconik login failed:") ||
        e.message?.includes("otp") ||
        e.message?.includes("OTP")
      ) {
        setError("Invalid or expired verification code. Try again.");
      } else {
        setError(e.message ?? "Something went wrong. Try again.");
      }
    } finally {
      setLaunching(false);
    }
  };

  const handleLaunchClick = () => {
    const creds = loadCreds();
    if (creds) {
      launch(creds);
    } else {
      setEmail("");
      setPassword("");
      setOtp("");
      setOtpType("totp");
      setMfaRequired(false);
      pendingCredsRef.current = null;
      setError(null);
      setShowModal(true);
    }
  };

  const handleSubmitCredentials = () => {
    if (email && password) {
      launch({ email, password });
    }
  };

  const handleSubmitOtp = () => {
    const creds = pendingCredsRef.current;
    if (creds && otp) {
      launch(creds, otpType, otp);
    }
  };

  return (
    <Wrapper>
      <SelectWrapper>
        <Select
          placeholder="Select an organization"
          value={systemDomainId}
          onChange={({ value }) => {
            navigate(
              location.pathname
                .replace(/dashboard\/[^/]*\//, `dashboard/${value}/`)
                .replace(/plugin\/.*/, "plugin")
            );
          }}
          optionsAsync={{
            key: `_/organization`,
            fn: async () =>
              (
                await ApiService.fetch<{ data: Option[] }>(
                  "GET",
                  `/api/select_option/_/organization`
                )
              ).data,
          }}
        ></Select>
      </SelectWrapper>

      {extensionReady && systemDomainId && (
        <Button
          icon={ExternalLink}
          label="Open in Iconik"
          type="default"
          onClick={handleLaunchClick}
          loading={launching}
          disabled={launching}
        />
      )}

      {showModal && (
        <ModalOverlay>
          <ClickOutside onClickOutside={closeModal}>
            <Section
              modal
              title="Sign in to Iconik"
              onClose={closeModal}
              width="480px"
              pad
              footer={
                <ModalButtons>
                  <Button
                    label="Cancel"
                    type="danger"
                    onClick={closeModal}
                    disabled={launching}
                  />
                  {!mfaRequired ? (
                    <Button
                      label="Open in Iconik"
                      type="success"
                      icon={ExternalLink}
                      onClick={handleSubmitCredentials}
                      loading={launching}
                      disabled={!email || !password || launching}
                    />
                  ) : (
                    <Button
                      label="Verify"
                      type="success"
                      onClick={handleSubmitOtp}
                      loading={launching}
                      disabled={!otp || launching}
                    />
                  )}
                </ModalButtons>
              }
            >
              {!mfaRequired ? (
                <>
                  <Input
                    label="Email"
                    value={email}
                    onChange={setEmail}
                    onSubmit={handleSubmitCredentials}
                  />
                  <Input
                    label="Password"
                    type="password"
                    value={password}
                    onChange={setPassword}
                    onSubmit={handleSubmitCredentials}
                  />
                </>
              ) : (
                <>
                  <Input
                    label="Verification code"
                    value={otp}
                    onChange={setOtp}
                    onSubmit={handleSubmitOtp}
                  />
                  <Input
                    label="Code type"
                    value={otpType}
                    onChange={setOtpType}
                  />
                </>
              )}
              {error && <Banner error={error} />}
            </Section>
          </ClickOutside>
        </ModalOverlay>
      )}
    </Wrapper>
  );
};

export default SelectOrganization;
